import {Pattern, match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  ASSISTANT_INTEREST_VEHICLES_MAX_ITEMS,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {ToSalesInterestAssistantContextInput} from '../types/ToSalesInterestAssistantContextInput';
import {toBoundedAssistantInterestText} from './toBoundedAssistantInterestText';
import {toResolvedVehicleLabel} from './toResolvedVehicleLabel';

export const toSalesInterestVehicles = (input: ToSalesInterestAssistantContextInput) => {
  const offeredVehicles = input.offeredVehicles.flatMap((vehicle) => {
    const label = toBoundedAssistantInterestText(vehicle.title);
    return isNotNil(label)
      ? [{id: vehicle.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS), label}]
      : [];
  });
  const minipurchaseMakes = (input.minipurchase?.filteredSpecifications?.make ?? []).flatMap(
    (make) => {
      const label = input.minipurchaseMakeOptions.find((option) => option.value === make)?.label;
      return isNotNil(label) ? [label] : [];
    }
  );
  const minipurchaseModels = (
    input.minipurchase?.filteredSpecifications?.modelFamily ?? []
  ).flatMap((model) => {
    const label =
      input.minipurchaseModelOptions.find((option) => option.value === model)?.label ?? model;
    const resolvedLabel = toResolvedVehicleLabel(label);
    return isNotNil(resolvedLabel) ? [resolvedLabel] : [];
  });
  const specificationLabel = toBoundedAssistantInterestText(
    [...minipurchaseMakes, ...minipurchaseModels].join(' ')
  );
  const specification = isNotNil(specificationLabel) ? [{label: specificationLabel}] : [];
  const minisaleMake = input.minisaleMakeOptions.find(
    (option) => option.value === input.minisale?.vehicleData?.make
  )?.label;
  const minisaleModel = toResolvedVehicleLabel(
    input.minisaleModelOptions.find(
      (option) => option.value === input.minisale?.vehicleData?.modelFamily
    )?.label ?? input.minisale?.vehicleData?.modelFamily
  );
  const tradeInVehicleLabel = toBoundedAssistantInterestText(
    [
      minisaleMake,
      minisaleModel,
      input.minisale?.vehicleData?.trim,
      input.minisale?.vehicleData?.variant,
    ]
      .filter(isNotNil)
      .join(' ')
  );
  const tradeInVehicle = match({interestType: input.interest.type, tradeInVehicleLabel})
    .with(
      {
        interestType: Pattern.union('SWAP', 'BUYING'),
        tradeInVehicleLabel: Pattern.string,
      },
      ({tradeInVehicleLabel: label}) => [{label}]
    )
    .otherwise(() => []);

  return [...offeredVehicles, ...specification, ...tradeInVehicle].slice(
    0,
    ASSISTANT_INTEREST_VEHICLES_MAX_ITEMS
  );
};
